import { PROFESSIONAL_TRIAL_LIMIT, PREMIUM_TRIAL_LIMIT } from "./tiers";
// The ONE definition of the public API and MCP counts (also read by the OpenAPI generator in utility-api).
import { API_COUNT_LABEL, MCP_COUNT_LABEL } from "../utility-api/scripts/public-counts.mjs";

/**
 * The facts the site, the emails and the sales deck all quote, in ONE place so they never disagree.
 * Source: the owner's Sales Deck v2 (October 2026). Update here and every surface follows.
 */
/**
 * Public user figure rule (owner, 28 Oct 2026): show the last milestone reached, with a "+". Below 10,000 users the milestones are
 * every 500 (5,000+, 5,500+, 6,000+ ...); from 10,000 up they are every 1,000 (10,000+, 11,000+, 12,000+ ...). Update USERS_REPORTED
 * with the owner's exact Google Analytics number whenever it is given; the label follows.
 */
export function usersMilestoneLabel(count: number): string {
  const step = count < 10000 ? 500 : 1000;
  const floor = Math.floor(count / step) * step;
  return `${floor.toLocaleString("en-US")}+`;
}
/** Exact users reported by the owner: Google Analytics "Active users", This year (Jan - 28 Oct 2026), shown as 5.3K. Replace with the exact figure when given. */
export const USERS_REPORTED = 5300;

/**
 * Tool-count rule (owner, 29 Oct 2026). Premium Applications and Workflow Products are tools too, so a sentence about the SITE as a
 * whole says "300+ professional tools, premium applications and workflow products" (toolCountLabel / toolCountPhrase). A box or page
 * about ONE category shows that category's own number (professionalToolCount, premiumApplicationCount, workflowProductCount; the
 * pages themselves compute the exact figure from the registry). The label is the total rounded DOWN to a hundred, so it can never
 * overstate; shared/siteFacts.counts.test.ts holds every one of these floors to the real registry.
 */
export function toolCountMilestone(total: number): string {
  return `${Math.floor(total / 100) * 100}+`;
}

export const SITE_FACTS = {
  /** General site claim: all visible tools (Professional + Premium + Workflow), rounded down to a hundred. Registry 29 Oct 2026: 251 + 104 + 4 = 359. Pinned by a test. */
  toolCountLabel: "300+",
  /** The general claim with its clarifying phrase. Use this wherever the whole site is described. */
  toolCountPhrase: "300+ professional tools, premium applications and workflow products",
  /** Professional Tools only (251 in the registry). Floor, for a box or email line about that category. Pinned by a test. */
  professionalToolCount: "250+",
  /** Premium Applications only (104 in the registry, equals PREMIUM_APP_IDS.length). Floor, pinned by a test. */
  premiumApplicationCount: "100+",
  /** Real count of Workflow Products (WORKFLOW_PRODUCT_IDS in shared/tiers.ts, pinned by a test). Never write "100+ workflow products". */
  workflowProductCount: "4",
  /** Premium Applications (104) plus Workflow Products (4), pinned by a test in shared/siteFacts.test.ts. Only for a line that is about those two categories alone; a line about the whole site uses toolCountPhrase. */
  premiumAndWorkflowCount: "100+",
  /**
   * REST endpoints on api.freedailypro.com (POST/GET /v1/... in utility-api/api/public/openapi.json). Public rule: a multiple of 50 with "+" (200+).
   * Defined once in utility-api/scripts/public-counts.mjs. shared/siteFacts.counts.test.ts fails when the floor exceeds the real number
   * or falls a full 50 behind it.
   */
  apiCount: API_COUNT_LABEL,
  /** Tools on the single MCP endpoint (createServer() in utility-api/mcp/src/index.ts). Same floor rule as apiCount. */
  mcpCount: MCP_COUNT_LABEL,
  /** Endpoints in the PowerPoint Toolkit API (utility-api/api/public/products/powerpoint-toolkit-api.openapi.json). Same floor rule. */
  pptxApiCount: "26",
  /**
   * People who have used the site. Owner statement of 28 Oct 2026: "more than 5,000 users". Say it as "more than 5,000 people use ...";
   * never as paying customers, ratings or compliance. This is the ONLY place the figure is written; every surface reads it from here.
   * The country, language and direct-traffic figures below are from Google Analytics (property FreeDailyPro, kottalgi.2022 account),
   * 1 Jan to 24 Oct 2026: 102 country rows (101 named countries), Direct = 60% of sessions (3,381 of 5,600).
   * Re-check all of them against Analytics when the owner next reports a new user number.
   */
  usage: { users: usersMilestoneLabel(USERS_REPORTED), countries: "100+", sessions: "5,600+", languages: "10", directPct: "60%" },
  /** Approved positioning line (owner, 9 Oct 2026). Keep the numbers beside it; they are what make it believable. */
  worldLeaderLine: "A world leader in comprehensive coverage of professional tools, applications, APIs and workflows.",
  /** Trial uses a day on Professional Tools. Read from shared/tiers.ts, the one place the trial numbers are set. */
  trialUsesPerDay: PROFESSIONAL_TRIAL_LIMIT,
  /** Trial uses a day on every Premium Application (owner decision, 28 Oct 2026: exactly 2, allowed and shown). */
  premiumTrialUsesPerDay: PREMIUM_TRIAL_LIMIT,
  dayPassPrice: "$1",
  monthlyPrice: "$4.99",
  supportEmail: "support@freedailypro.com",
  demoVideoMinutes: "4",
  /** Official YouTube video id of the platform tour on the @freedailypro channel (newer re-make, 2026-10-28; it replaces the 2026-10-14 upload 8lQfd7EbT6g, which stays on the channel as the older duplicate). */
  youtubeTourId: "s1CA84t_PCA",
  youtubeChannel: "https://www.youtube.com/@freedailypro",
} as const;

/** Meta descriptions that quote the counts. Built here so a test can hold them to 160 characters and one sentence each. */
export const HOME_META_DESCRIPTION = `${SITE_FACTS.toolCountPhrase}, ${SITE_FACTS.apiCount} API endpoints, ${SITE_FACTS.mcpCount} MCP tools and custom software. Private, in your browser.`;
export const DEVELOPERS_META_DESCRIPTION = `${SITE_FACTS.apiCount} REST endpoints and ${SITE_FACTS.mcpCount} MCP tools: PDF, PowerPoint, documents, calculators, media and text. One API key, nothing stored.`;
